import { create } from "zustand";
import { api } from "@/lib/api";

// ── Types ────────────────────────────────────────────────────────────

export const CONTENT_STAGES = [
  { id: "scheduled",    label: "Scheduled",  color: "#9CA3AF" },
  { id: "queued",       label: "Queue",      color: "#6B7280" },
  { id: "researching",  label: "Research",   color: "#3B82F6" },
  { id: "outlining",    label: "Outline",    color: "#6366F1" },
  { id: "writing",      label: "Writing",    color: "#8B5CF6" },
  { id: "reviewing",    label: "Review",     color: "#F59E0B" },
  { id: "approved",     label: "Approved",   color: "#10B981" },
  { id: "published",    label: "Published",  color: "#065F46" },
] as const;

export type ContentStage = typeof CONTENT_STAGES[number]["id"];

export const STAGE_COLOR_MAP: Record<string, string> = {
  ...Object.fromEntries(CONTENT_STAGES.map((s) => [s.id, s.color])),
  error: "#EF4444",
  killed: "#6B7280",
};

export type ContentTab = "board" | "calendar" | "keywords" | "stats";

export const FREQUENCY_OPTIONS = [
  { value: "1", label: "1x / week" },
  { value: "2", label: "2x / week" },
  { value: "3", label: "3x / week" },
  { value: "7", label: "Daily" },
] as const;

/** Type-safe accessor for ContentItem.metadata fields */
export function getItemMeta<T>(item: ContentItem, key: string): T | undefined {
  return (item.metadata as Record<string, unknown>)?.[key] as T | undefined;
}

export interface ContentItem {
  id: string;
  project_id: number;
  task_id: string | null;
  title: string;
  slug: string | null;
  content_type: string;
  stage: ContentStage | "error" | "killed";
  primary_keyword: string | null;
  secondary_keywords: string[];
  outline: Record<string, unknown> | null;
  draft_content: string | null;
  final_content: string | null;
  meta_title: string | null;
  meta_description: string | null;
  word_count: number;
  target_word_count: number | null;
  scheduled_date: string | null;
  published_url: string | null;
  published_at: string | null;
  blog_idea: string | null;
  scheduled_publish_date: string | null;
  auto_social: boolean | null;
  metadata: Record<string, unknown>;
  // WP6 content workflow fields (dedicated DB columns)
  research_brief: string | null;
  drafts: Record<string, unknown>[];
  revision_count: number;
  ai_detection_score: number | null;
  keyword_density: number | null;
  workflow_run_id: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublishConfig {
  blog_repo: string;
  blog_path: string;
  blog_base_url: string;
  target_branch: string;
  publish_mode: "direct_push" | "pull_request";
  rebuild_webhook_url: string;
}

export interface ContentProjectConfig {
  id: number;
  project_id: number;
  target_audience: string | null;
  brand_voice: string | null;
  default_language: string;
  seo_guidelines: string | null;
  publishing_platforms: string[];
  content_templates: Record<string, unknown>;
  publish_config?: Partial<PublishConfig>;
  keyword_clusters?: string[];
  research_schedule?: {
    enabled: boolean;
    frequency: string;
    max_keywords_per_run: number;
  };
  created_at: string;
  updated_at: string;
}

export interface KeywordResearch {
  id: number;
  project_id: number;
  keyword: string;
  search_volume: number | null;
  keyword_difficulty: number | null;
  cpc: number | null;
  intent: string | null;
  cluster: string | null;
  priority: string;
  status: string;
  content_item_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  content_item_id: string;
  platform: string;
  content: string;
  status: "draft" | "pending_review" | "scheduled" | "posted" | "failed";
  scheduled_at: string | null;
  posted_at: string | null;
  external_url: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ScheduleSlot {
  id: string;
  project_id: number;
  scheduled_date: string;
  content_item_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateContentItemInput {
  title: string;
  content_type?: string;
  stage?: string;
  primary_keyword?: string;
  blog_idea?: string;
  target_word_count?: number;
  scheduled_publish_date?: string;
  auto_social?: boolean;
  metadata?: Record<string, unknown>;
}

export interface ReviewAction {
  action: "approve" | "revise" | "kill";
  feedback?: string;
}

// Stable empty arrays — prevents infinite re-render loops in Zustand selectors.
// Using `|| []` in a selector creates a new array ref each render → Object.is fails → loop.
export const EMPTY_ITEMS: ContentItem[] = [];
export const EMPTY_KEYWORDS: KeywordResearch[] = [];
export const EMPTY_SOCIAL_POSTS: SocialPost[] = [];

// ── Store ────────────────────────────────────────────────────────────

interface ContentState {
  // Per-project data (keyed by projectId string)
  items: Record<string, ContentItem[]>;
  config: Record<string, ContentProjectConfig>;
  keywords: Record<string, KeywordResearch[]>;
  calendarSlots: Record<string, ScheduleSlot[]>;
  // Per-item data (keyed by content item id)
  socialPosts: Record<string, SocialPost[]>;

  // UI state
  selectedItemId: string | null;
  calendarView: "month" | "week";
  activeTab: ContentTab;

  // Loading states
  loading: Record<string, boolean>;

  // Actions — Content Items
  fetchItems: (projectId: number) => Promise<void>;
  createItem: (projectId: number, input: CreateContentItemInput) => Promise<ContentItem>;
  updateItem: (itemId: string, updates: Partial<ContentItem>) => Promise<void>;
  deleteItem: (itemId: string, projectId: number) => Promise<void>;
  moveItemToStage: (itemId: string, projectId: number, newStage: ContentStage) => Promise<void>;

  // Actions — Config
  fetchConfig: (projectId: number) => Promise<void>;
  updateConfig: (projectId: number, config: Partial<ContentProjectConfig>) => Promise<void>;

  // Actions — Keywords
  fetchKeywords: (projectId: number) => Promise<void>;
  createKeyword: (projectId: number, keyword: string) => Promise<KeywordResearch>;
  deleteKeyword: (keywordId: number, projectId: number) => Promise<void>;
  runKeywordResearch: (projectId: number) => Promise<{ keywords_found: number }>;
  queueKeywordAsContent: (keywordId: number, projectId: number) => Promise<void>;

  // Actions — Calendar / Schedule
  fetchCalendarSlots: (projectId: number) => Promise<void>;
  createScheduleSlot: (projectId: number, date: string, contentItemId?: string) => Promise<ScheduleSlot>;
  deleteScheduleSlot: (slotId: string, projectId: number) => Promise<void>;
  rescheduleItem: (itemId: string, newDate: string) => Promise<void>;

  // Actions — Social Posts
  fetchSocialPosts: (contentItemId: string) => Promise<void>;
  generateSocialPosts: (contentItemId: string, platforms?: string[]) => Promise<SocialPost[]>;
  updateSocialPost: (postId: string, contentItemId: string, data: Partial<SocialPost>) => Promise<void>;
  publishSocialPost: (postId: string, contentItemId: string) => Promise<void>;
  publishAllSocialPosts: (contentItemId: string) => Promise<void>;

  // Actions — Pipeline
  startPipeline: (projectId: number, itemId: string) => Promise<void>;
  refreshItem: (itemId: string, projectId: number) => Promise<void>;

  // Actions — Review
  submitReview: (itemId: string, projectId: number, review: ReviewAction) => Promise<void>;

  // UI actions
  selectItem: (itemId: string | null) => void;
  setCalendarView: (view: "month" | "week") => void;
  setActiveTab: (tab: ContentTab) => void;
  setLoading: (key: string, value: boolean) => void;
}

/**
 * Content pipeline Zustand store.
 *
 * All data is keyed by projectId (items, config, keywords, calendar)
 * or by contentItemId (socialPosts). Actions are optimistic where possible.
 */
export const useContentStore = create<ContentState>((set, get) => ({
  items: {},
  config: {},
  keywords: {},
  calendarSlots: {},
  socialPosts: {},
  selectedItemId: null,
  calendarView: "month",
  activeTab: "board",
  loading: {},

  setLoading: (key, value) =>
    set((s) => ({ loading: { ...s.loading, [key]: value } })),

  // ── Content Items ──────────────────────────────────────────────────

  fetchItems: async (projectId) => {
    const key = `items-${projectId}`;
    get().setLoading(key, true);
    try {
      const data = await api.get<ContentItem[]>(
        `/api/projects/${projectId}/content-items`
      );
      set((s) => ({ items: { ...s.items, [projectId]: data } }));
    } finally {
      get().setLoading(key, false);
    }
  },

  createItem: async (projectId, input) => {
    const item = await api.post<ContentItem>(
      `/api/projects/${projectId}/content-items`,
      { project_id: projectId, ...input }
    );
    set((s) => ({
      items: {
        ...s.items,
        [projectId]: [...(s.items[projectId] || []), item],
      },
    }));
    return item;
  },

  updateItem: async (itemId, updates) => {
    const updated = await api.patch<ContentItem>(
      `/api/content-items/${itemId}`,
      updates
    );
    set((s) => {
      const newItems = { ...s.items };
      const pid = String(updated.project_id);
      if (newItems[pid]) {
        newItems[pid] = newItems[pid].map((i) =>
          i.id === itemId ? updated : i
        );
      }
      return { items: newItems };
    });
  },

  deleteItem: async (itemId, projectId) => {
    await api.delete(`/api/content-items/${itemId}`);
    set((s) => ({
      items: {
        ...s.items,
        [projectId]: (s.items[projectId] || []).filter((i) => i.id !== itemId),
      },
    }));
  },

  moveItemToStage: async (itemId, projectId, newStage) => {
    const pid = String(projectId);
    const prevStage = (get().items[pid] || []).find((i) => i.id === itemId)?.stage;

    // Optimistic update
    set((s) => ({
      items: {
        ...s.items,
        [pid]: (s.items[pid] || []).map((i) =>
          i.id === itemId ? { ...i, stage: newStage } : i
        ),
      },
    }));

    try {
      await api.patch(`/api/content-items/${itemId}`, { stage: newStage });
    } catch {
      // Rollback: restore only this item's stage field on the current state,
      // so any concurrent change to other items (or this item's other fields)
      // survives.
      if (prevStage !== undefined) {
        set((s) => ({
          items: {
            ...s.items,
            [pid]: (s.items[pid] || []).map((i) =>
              i.id === itemId ? { ...i, stage: prevStage } : i
            ),
          },
        }));
      }
      throw new Error("Failed to update stage");
    }
  },

  // ── Config ─────────────────────────────────────────────────────────

  fetchConfig: async (projectId) => {
    try {
      const data = await api.get<ContentProjectConfig>(
        `/api/projects/${projectId}/content-config`
      );
      set((s) => ({ config: { ...s.config, [projectId]: data } }));
    } catch {
      // 404 = no config yet, that's fine
    }
  },

  updateConfig: async (projectId, configUpdates) => {
    const data = await api.put<ContentProjectConfig>(
      `/api/projects/${projectId}/content-config`,
      configUpdates
    );
    set((s) => ({ config: { ...s.config, [projectId]: data } }));
  },

  // ── Keywords ───────────────────────────────────────────────────────

  fetchKeywords: async (projectId) => {
    const key = `keywords-${projectId}`;
    get().setLoading(key, true);
    try {
      const data = await api.get<KeywordResearch[]>(
        `/api/projects/${projectId}/keywords`
      );
      set((s) => ({ keywords: { ...s.keywords, [projectId]: data } }));
    } finally {
      get().setLoading(key, false);
    }
  },

  createKeyword: async (projectId, keyword) => {
    const kw = await api.post<KeywordResearch>(
      `/api/projects/${projectId}/keywords`,
      { project_id: projectId, keyword }
    );
    set((s) => ({
      keywords: {
        ...s.keywords,
        [projectId]: [...(s.keywords[projectId] || []), kw],
      },
    }));
    return kw;
  },

  deleteKeyword: async (keywordId, projectId) => {
    await api.delete(`/api/keywords/${keywordId}`);
    set((s) => ({
      keywords: {
        ...s.keywords,
        [projectId]: (s.keywords[projectId] || []).filter(
          (k) => k.id !== keywordId
        ),
      },
    }));
  },

  runKeywordResearch: async (projectId) => {
    const key = `research-${projectId}`;
    get().setLoading(key, true);
    try {
      const result = await api.post<{ status: string; job_id: number | null }>(
        `/api/projects/${projectId}/keywords/research`
      );
      // Refresh keywords list (job may still be running, but show current state)
      await get().fetchKeywords(projectId);
      return { keywords_found: 0, ...result };
    } finally {
      get().setLoading(key, false);
    }
  },

  queueKeywordAsContent: async (keywordId, projectId) => {
    await api.post(`/api/keywords/${keywordId}/queue`);
    // Refresh both keywords and items
    await Promise.all([
      get().fetchKeywords(projectId),
      get().fetchItems(projectId),
    ]);
  },

  // ── Calendar / Schedule ────────────────────────────────────────────

  fetchCalendarSlots: async (projectId) => {
    const data = await api.get<ScheduleSlot[]>(
      `/api/projects/${projectId}/schedule`
    );
    set((s) => ({ calendarSlots: { ...s.calendarSlots, [projectId]: data } }));
  },

  createScheduleSlot: async (projectId, date, contentItemId) => {
    const slot = await api.post<ScheduleSlot>(
      `/api/projects/${projectId}/schedule`,
      {
        project_id: projectId,
        scheduled_date: date,
        content_item_id: contentItemId || null,
      }
    );
    set((s) => ({
      calendarSlots: {
        ...s.calendarSlots,
        [projectId]: [...(s.calendarSlots[projectId] || []), slot],
      },
    }));
    return slot;
  },

  deleteScheduleSlot: async (slotId, projectId) => {
    await api.delete(`/api/schedule/${slotId}`);
    set((s) => ({
      calendarSlots: {
        ...s.calendarSlots,
        [projectId]: (s.calendarSlots[projectId] || []).filter(
          (sl) => sl.id !== slotId
        ),
      },
    }));
  },

  rescheduleItem: async (itemId, newDate) => {
    // Find which project key the item lives under, and remember its
    // previous scheduled_publish_date so a failed request can restore just
    // that one field on that one item.
    const allItems = get().items;
    let targetPid: string | undefined;
    let prevDate: string | null | undefined;
    for (const pid of Object.keys(allItems)) {
      const item = allItems[pid]?.find((i) => i.id === itemId);
      if (item) {
        targetPid = pid;
        prevDate = item.scheduled_publish_date;
        break;
      }
    }

    if (targetPid) {
      const pid = targetPid;
      set((s) => ({
        items: {
          ...s.items,
          [pid]: (s.items[pid] || []).map((i) =>
            i.id === itemId ? { ...i, scheduled_publish_date: newDate } : i
          ),
        },
      }));
    }

    try {
      await api.patch(`/api/content-items/${itemId}`, {
        scheduled_publish_date: newDate,
      });
    } catch {
      // Rollback: restore only this item's scheduled_publish_date on the
      // current state. If the item is gone from current state, this is a
      // correct no-op.
      if (targetPid) {
        const pid = targetPid;
        set((s) => ({
          items: {
            ...s.items,
            [pid]: (s.items[pid] || []).map((i) =>
              i.id === itemId ? { ...i, scheduled_publish_date: prevDate ?? null } : i
            ),
          },
        }));
      }
      throw new Error("Failed to reschedule");
    }
  },

  // ── Social Posts ───────────────────────────────────────────────────

  fetchSocialPosts: async (contentItemId) => {
    const data = await api.get<SocialPost[]>(
      `/api/content-items/${contentItemId}/social-posts`
    );
    set((s) => ({ socialPosts: { ...s.socialPosts, [contentItemId]: data } }));
  },

  generateSocialPosts: async (contentItemId, platforms) => {
    const body: Record<string, unknown> = {};
    if (platforms) body.platforms = platforms;
    const data = await api.post<SocialPost[]>(
      `/api/content-items/${contentItemId}/social-posts/generate`,
      body
    );
    set((s) => ({ socialPosts: { ...s.socialPosts, [contentItemId]: data } }));
    return data;
  },

  updateSocialPost: async (postId, contentItemId, data) => {
    const updated = await api.patch<SocialPost>(`/api/social-posts/${postId}`, data);
    set((s) => ({
      socialPosts: {
        ...s.socialPosts,
        [contentItemId]: (s.socialPosts[contentItemId] || []).map((p) =>
          p.id === postId ? updated : p
        ),
      },
    }));
  },

  publishSocialPost: async (postId, contentItemId) => {
    const updated = await api.post<SocialPost>(`/api/social-posts/${postId}/publish`, {});
    set((s) => ({
      socialPosts: {
        ...s.socialPosts,
        [contentItemId]: (s.socialPosts[contentItemId] || []).map((p) =>
          p.id === postId ? updated : p
        ),
      },
    }));
  },

  publishAllSocialPosts: async (contentItemId) => {
    await api.post(`/api/content-items/${contentItemId}/social-posts/publish-all`, {});
    // Refetch — publish-all updates multiple posts
    const posts = await api.get<SocialPost[]>(
      `/api/content-items/${contentItemId}/social-posts`
    );
    set((s) => ({ socialPosts: { ...s.socialPosts, [contentItemId]: posts } }));
  },

  // ── Pipeline ────────────────────────────────────────────────────────

  startPipeline: async (projectId, itemId) => {
    const pid = String(projectId);
    const prevItem = (get().items[pid] || []).find((i) => i.id === itemId);
    const prevStage = prevItem?.stage;
    const prevError = prevItem?.last_error;

    // Optimistic update: move to queued (matches backend), clear any previous error
    set((s) => ({
      items: {
        ...s.items,
        [pid]: (s.items[pid] || []).map((i) =>
          i.id === itemId ? { ...i, stage: "queued" as ContentStage, last_error: null } : i
        ),
      },
    }));

    try {
      await api.post(`/api/content-items/${itemId}/start`, {});
    } catch {
      // Rollback: restore only this item's stage/last_error fields on the
      // current state, so concurrent changes to other items survive.
      if (prevStage !== undefined) {
        set((s) => ({
          items: {
            ...s.items,
            [pid]: (s.items[pid] || []).map((i) =>
              i.id === itemId ? { ...i, stage: prevStage, last_error: prevError ?? null } : i
            ),
          },
        }));
      }
      throw new Error("Failed to start pipeline");
    }
  },

  refreshItem: async (itemId, projectId) => {
    const item = await api.get<ContentItem>(`/api/content-items/${itemId}`);
    set((s) => {
      const pid = String(projectId);
      const items = (s.items[pid] || []).map((i) =>
        i.id === itemId ? item : i
      );
      return { items: { ...s.items, [pid]: items } };
    });
  },

  // ── Review ─────────────────────────────────────────────────────────

  submitReview: async (itemId, projectId, review) => {
    const stageMap: Record<string, ContentStage> = {
      approve: "approved",
      revise: "writing",
    };
    const pid = String(projectId);

    await api.post(`/api/content-items/${itemId}/review`, {
      action: review.action,
      feedback: review.feedback || "",
    });

    if (review.action === "kill") {
      set((s) => ({
        items: { ...s.items, [pid]: (s.items[pid] || []).filter((i) => i.id !== itemId) },
      }));
    } else {
      const newStage = stageMap[review.action];
      if (newStage) {
        set((s) => {
          const items = (s.items[pid] || []).map((i) =>
            i.id === itemId ? { ...i, stage: newStage } : i
          );
          return { items: { ...s.items, [pid]: items } };
        });
      }
    }
  },

  // ── UI State ───────────────────────────────────────────────────────

  selectItem: (itemId) => set({ selectedItemId: itemId }),
  setCalendarView: (view) => set({ calendarView: view }),
  setActiveTab: (tab) => set({ activeTab: tab }),
}));
